import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {useChangeVisibilityMutation, useGetGroupFiltersQuery} from '../../../../api/services/groupService';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import GroupFilterSettingsTree from './GroupFilterSettingsTree';
import useNotification from '../../../../utility/hooks/useNotification';
import AdmicityBackdrop from '../../../../shared-components/AdmicityBackdrop';
import PropTypes from 'prop-types';
import {SMALL_BLUE_BUTTON_SX} from '../SettingsPills';

const rootNodesWithVisibilityChangeAllowed = ['EMPLOYEES', 'ENQUIRY', 'FUTURE-ADMISSION'];

const GroupFilters = ({schoolInfo}) => {
    const {
        data: groupFilters,
        refetch: refetchGroupFilters,
        isFetching: isRefetchingGroupFilters,
        isLoading: isLoadingGroupFilters
    } = useGetGroupFiltersQuery({
        fetchOnlyVisible: false,
        maxLevel: 2
    });
    const [changeVisibility, {
        isSuccess: isChangeVisibilitySuccess,
        isError: isChangeVisibilityError,
        isLoading: isChangingVisibilityInProgress
    }] = useChangeVisibilityMutation();

    const [modifiedFilters, setModifiedFilters] = useState({});
    const {
        showErrorNotification,
        showSuccessNotification
    } = useNotification();

    useEffect(() => {
        if (isChangeVisibilityError) {
            showErrorNotification('Failed to update group filter visibility settings');
        } else if (isChangeVisibilitySuccess) {
            refetchGroupFilters();
            showSuccessNotification('Group filter visibility settings have been updated successfully');
        }
    }, [isChangeVisibilitySuccess, isChangeVisibilityError, refetchGroupFilters]);

    useEffect(() => {
        if (isRefetchingGroupFilters) {
            setModifiedFilters({});
        }
    }, [isRefetchingGroupFilters]);

    const handleTreeItemVisibilityChange = useCallback((node, makeVisible) =>
        setModifiedFilters(prev => {
            if (Object.prototype.hasOwnProperty.call(prev, node.nodeId) && node.isVisible === makeVisible) {
                const {
                    [node.nodeId]: _,
                    ...rest
                } = prev;

                return rest;
            }

            return {
                ...prev,
                [node.nodeId]: makeVisible
            };
        }), []);

    const filteredModules = useMemo(() => {
        return groupFilters?.modules?.filter(
            (module) => module.name === 'PRE-ADMISSION' || module.name === 'ON-ROLL' || module.name === 'FUTURE-ADMISSION' || module.name === 'EMPLOYEES' || (!schoolInfo.disableEnquiriesSync && module.name === 'ENQUIRY')
        ) || [];
    }, [groupFilters]);

    return (
        <Box
            sx={{
                // Maria, 20 October 2026: less space at the top
                mt: 0.75,
                width: {
                    xs: '100%',
                },
                mx: 'auto',
                position: 'relative',
            }}
        >
            {!isLoadingGroupFilters && filteredModules.length > 0
                ? (
                    // Maria, 20 October 2026: Save at the top and the filters in a white card; the words from the Group
                    // Filter Visibility video script are on the Filters tab
                    <>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5}}>
                            <Box/>
                            <Button
                                sx={SMALL_BLUE_BUTTON_SX}
                                variant="contained"
                                data-testid="filters-save"
                                disabled={isChangingVisibilityInProgress || Object.keys(modifiedFilters).length === 0}
                                onClick={async () =>
                                    await changeVisibility({
                                        groupFilters: Object.entries(modifiedFilters).map(([id, isVisible]) => ({
                                            id,
                                            isVisible
                                        }))
                                    })
                                }
                            >
                                Save
                            </Button>
                        </Box>
                        <Box sx={{backgroundColor: '#fff', border: '1px solid #e3e8ef', borderRadius: 2, px: 1, py: 0.5}}>
                            <GroupFilterSettingsTree
                                data={filteredModules}
                                rootNodesWithVisibilityChangeAllowed={rootNodesWithVisibilityChangeAllowed}
                                onVisibilityChange={handleTreeItemVisibilityChange}
                            />
                        </Box>
                    </>
                )
                : (
                    <Box paddingTop={2}>
                        <Typography variant="h5" gutterBottom>
                            No Group Filters Available
                        </Typography>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                            {'Group filters may not be available because they haven\'t been synchronized yet, or an error occurred while retrieving them. Please try again later.'}
                        </Typography>
                    </Box>
                )}
            <AdmicityBackdrop
                open={isLoadingGroupFilters || isRefetchingGroupFilters || isChangingVisibilityInProgress}
            />
        </Box>
    );
};

GroupFilters.propTypes = {
    schoolInfo: PropTypes.object
};

export default GroupFilters;
